import React, { useEffect } from "react";
import RegisterHeader from "./Common/Header";
import RegisterSteps from "./Common/Steps";
import {
  Button,
  Container,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Box,
  Card,
} from "@mui/material";
import "./Register.scss";
import { useRegister } from "../../context/RegisterContext";
import { useNavigate } from "react-router-dom";
import { REQUIRED_FIELD } from "../../utils/constants";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Owner } from "../../types/RegisterType";
import CpfMaskComponent from "../InputMask/CpfMask";
import { cpfValidator } from "../../utils/cpf-validator";
import Footer from "../Footer";
import ReactGA from "react-ga4";

const RegisterOwner = () => {
  const navigate = useNavigate();
  const { registerOwner, getRegisterData, canProceed } = useRegister();
  
  const steps = [
    'Endereço',
    'Responsável',
    'Negócio',
    'Explicação',
    'Categoria',
    'Login',
    'Confirmação'
  ];

  const schemaOwner = yup.object({
    full_name: yup.string().required(REQUIRED_FIELD),
    document: yup
      .string()
      .required(REQUIRED_FIELD)
      .test("cpf", "CPF Inválido", cpfValidator),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schemaOwner),
  });

  const onSubmitOwner = (data: Owner) => {
    registerOwner(data);
    navigate("/register/carwash");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!canProceed()) {
      navigate("/parceiros");
      return;
    }

    const { owner } = getRegisterData();
    setValue("full_name", `${owner?.full_name as string}${owner?.last_name ? ` ${owner?.last_name as string}` : ""}`);
    setValue("document", owner?.document as string);
  }, []);

  return (
    <div className="Register" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
    }}>
      <RegisterHeader />

      <Container maxWidth="md" className="MainContainer">
        <RegisterSteps currentStep={2} steps={steps} />
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h1" sx={{ 
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 'bold',
            color: '#0f172a',
            marginBottom: 2
          }}>
            Responsável da loja
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Informe os dados da pessoa que tem o nome no contrato social da
            empresa, seja como dona, sócia ou sócia administrativa.
          </Typography>
        </Box>

        <Card sx={{ 
          maxWidth: '600px', 
          margin: '0 auto', 
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          backgroundColor: 'white'
        }}>
          <form onSubmit={handleSubmit(onSubmitOwner)}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="full_name"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="full_name">Nome completo</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="full_name"
                        type="text"
                        placeholder="Nome do responsável legal"
                        fullWidth
                        error={!!errors.full_name}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.full_name?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="document"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="document">
                        CPF do responsável legal
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="document"
                        type="text"
                        placeholder="000.000.000-00"
                        fullWidth
                        error={!!errors.document}
                        inputComponent={CpfMaskComponent as any}
                      />
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        * Esse CPF não pode estar cadastrado no app como cliente.
                      </Typography>
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.document?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate("/register/address")}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderColor: '#d1d5db',
                    color: '#374151',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                      borderColor: '#9ca3af',
                    },
                  }}
                >
                  Voltar
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  onClick={() => {
                    ReactGA.event("continue_store_click", {
                      event: 'continue_store_click',
                      button_id: 'continue_store',
                      page_path: window.location.pathname
                    })
                  }}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Card>
        
        <Box sx={{ textAlign: 'center', marginTop: 4, marginBottom: 2 }}>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: '1rem'
          }}>
            Ficou alguma dúvida?{" "}
            <a 
              href="/fale-conosco" 
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Fale Conosco
            </a>
          </Typography>
        </Box>
      </Container>

      <Footer />
    </div>
  );
};

export default RegisterOwner;
